/**
 * The supported zoom range, applied in **two** places that must agree: `<Map minZoom maxZoom>`
 * bounds every gesture (113584 §5e — zooming beyond the range ends without glitching or an error),
 * and {@link stepZoom} bounds the controls (§6c — the control at a limit is shown as unavailable).
 * Neither the acceptance criteria nor any Figma frame gives a value; 20 is Google's satellite
 * ceiling across most US imagery and 3 shows a continent.
 */
export const MIN_ZOOM = 3;
export const MAX_ZOOM = 20;

/**
 * One zoom step, clamped to the supported range (113584 §6b). Returns the **current** zoom when the
 * step would leave the range, so "at a limit the control is unavailable and activating it does
 * nothing" is one equality check at the call site rather than a separate predicate and an error path.
 *
 * A trackpad or pinch gesture leaves Google at a **fractional** zoom, so the step snaps to the next
 * whole level in the direction asked for rather than adding to the fraction (which would carry it
 * through every later press) — and rather than rounding first, which skips a level from the far side
 * of the midpoint: from 15.62, rounding gives 16 and then 17, when the next level up is 16.
 */
export function stepZoom(current: number, delta: 1 | -1): number {
  const next = delta === 1 ? Math.floor(current) + 1 : Math.ceil(current) - 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next));
}
